"use client"

import type React from "react"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { CheckCircle2, Upload, X, Youtube, AlertCircle, ChevronDown, Loader2, Coins, Rocket, Star, Gift } from "lucide-react"
import { Icons8Icon } from "@/components/ui/icons8-icon"
import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Switch } from "@/components/ui/switch"
import { useAccount, useChainId, useReadContract } from 'wagmi'
import { useSwitchChain } from 'wagmi'
import { parseEther, formatUnits, formatEther } from 'viem'
import { useCreateMultiCurrencyPresale, PAYMENT_TOKENS, PaymentToken, useCreateFairLaunch, ERC20_ABI, getTokenLockerAddress } from '@/lib/contract-hooks'
import { isTestnet, TESTNET_CHAIN_IDS, MAINNET_CHAIN_IDS } from '@/lib/contracts-config'
import { getCryptoPrice } from '@/lib/price-service'
import {
  MULTICHAIN_MODE,
  isSequentialMode,
  isRelayerMode,
  RELAYER_API_URL,
  CHAIN_CONFIGS,
  getChainConfig
} from '@/lib/multichain-config'
import { getDexRouter } from '@/lib/dex-config'

const ERC20_ABI_LOCAL = [
  { inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'decimals', outputs: [{ type: 'uint8' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'totalSupply', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
  { inputs: [{ name: 'account', type: 'address' }], name: 'balanceOf', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
] as const

export function CreatePresaleContent() {
  const router = useRouter()
  const chainId = useChainId()
  const { isConnected, address } = useAccount()
  const { switchChain, isPending: isSwitchingNetwork } = useSwitchChain()

  // Step management
  const [activeStep, setActiveStep] = useState(0)

  // Token Info
  const [tokenAddress, setTokenAddress] = useState("")
  const [tokenVerified, setTokenVerified] = useState(false)
  const [tokenInfo, setTokenInfo] = useState<{
    name: string
    symbol: string
    decimals: number
    totalSupply: bigint
    balance: bigint
  } | null>(null)

  // Presale Parameters
  const [presaleRate, setPresaleRate] = useState("") // tokens per BNB
  const [listingRate, setListingRate] = useState("") // DEX listing rate
  const [softCap, setSoftCap] = useState("")
  const [hardCap, setHardCap] = useState("")
  const [minContribution, setMinContribution] = useState("0.01")
  const [maxContribution, setMaxContribution] = useState("5")
  const [startTime, setStartTime] = useState("")
  const [endTime, setEndTime] = useState("")
  const [liquidityPercent, setLiquidityPercent] = useState("60")
  const [tokenAmount, setTokenAmount] = useState("") // Amount of tokens to sell

  // Project Details
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null) // Uploaded logo URL
  const [videoType, setVideoType] = useState<"upload" | "youtube">("upload")
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [youtubeLink, setYoutubeLink] = useState("")
  const [shortDescription, setShortDescription] = useState("")
  const [description, setDescription] = useState("")
  const [website, setWebsite] = useState("")
  const [twitter, setTwitter] = useState("")
  const [telegram, setTelegram] = useState("")

  const [customSlug, setCustomSlug] = useState("") // Custom URL like "nifi-token"

  // DEX Selection
  const [selectedDex, setSelectedDex] = useState("pancakeswap-v2")
  const [dexRouter, setDexRouter] = useState(getDexRouter("pancakeswap-v2") || "")

  // Tokenomics
  const [tokenomics, setTokenomics] = useState<{ name: string; percent: number; wallet: string }[]>([
    { name: "Presale", percent: 50, wallet: "" },
    { name: "Liquidity", percent: 30, wallet: "" },
    { name: "Team", percent: 10, wallet: "" },
    { name: "Marketing", percent: 10, wallet: "" },
  ])

  // LP Locking Options
  const [lockLiquidity, setLockLiquidity] = useState(true)
  const [lpLockDays, setLpLockDays] = useState("365")

  // Options
  const [isFairLaunch, setIsFairLaunch] = useState(false)
  const [isPremium, setIsPremium] = useState(false)
  const [keyFeatures, setKeyFeatures] = useState<string[]>([""])
  const [auditType, setAuditType] = useState<"link" | "upload">("link")
  const [auditValue, setAuditValue] = useState("")
  const [kycType, setKycType] = useState<"link" | "upload">("link")
  const [kycValue, setKycValue] = useState("")
  const [isKYC, setIsKYC] = useState(false)
  const [isAudit, setIsAudit] = useState(false)

  // Claim Settings
  const [allowEarlyClaim, setAllowEarlyClaim] = useState(false)
  const [unsoldTokenAction, setUnsoldTokenAction] = useState<"burn" | "refund">("refund")

  // Accepted Currencies & Networks
  const [acceptedCurrencies, setAcceptedCurrencies] = useState({
    BNB: true,
    USDT: true,
    ETH: false,
    USDC: false,
    DAI: false,
  })
  const [acceptedNetworks, setAcceptedNetworks] = useState({
    BSC: true,
    ETH: false,
    POLYGON: false,
    BASE: false,
  })

  // Referral Settings
  const [enableReferral, setEnableReferral] = useState(true)
  const [referralPercent, setReferralPercent] = useState("5")

  // Real-time crypto prices from Binance/CoinGecko
  const [cryptoPrices, setCryptoPrices] = useState<Record<string, number>>({
    BNB: 700,   // Default fallbacks
    ETH: 3500,
    MATIC: 0.5,
  })
  const [priceLoading, setPriceLoading] = useState(false)

  // Hydration fix
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Rate input type: 'usd' (tokens per $1) or 'native' (tokens per 1 BNB/ETH/etc)
  const [rateType, setRateType] = useState<'usd' | 'native'>('usd')

  // Per-currency and per-network limits
  const [perCurrencyLimits, setPerCurrencyLimits] = useState<Record<string, { min: string, max: string, enabled: boolean }>>({
    BNB: { min: "0.01", max: "10", enabled: false },
    USDT: { min: "10", max: "5000", enabled: false },
    ETH: { min: "0.005", max: "2", enabled: false },
    USDC: { min: "10", max: "5000", enabled: false },
    DAI: { min: "10", max: "5000", enabled: false },
  })

  // Multi-chain deployment progress
  const [deploymentProgress, setDeploymentProgress] = useState<{
    currentNetwork: string | null
    completedNetworks: string[]
    deployedAddresses: Record<string, string>
    isMultiDeploying: boolean
  }>({
    currentNetwork: null,
    completedNetworks: [],
    deployedAddresses: {},
    isMultiDeploying: false
  })

  // Transaction step tracking
  const [txStep, setTxStep] = useState<{
    current: number
    total: number
    description: string
  }>({ current: 0, total: 0, description: '' })

  const [deployError, setDeployError] = useState<string | null>(null)
  const [isRelayerProcessing, setIsRelayerProcessing] = useState(false)

  // Two-step approval flow
  const [isApprovalStep, setIsApprovalStep] = useState(true) // Start with approval
  const [isApproved, setIsApproved] = useState(false)

  // Network configuration
  const networks = isTestnet
    ? [
      { id: "BSC", name: "BSC Testnet", fullName: "BSC Testnet", chainId: TESTNET_CHAIN_IDS.BSC_TESTNET },
      { id: "ETH", name: "Sepolia", fullName: "Sepolia Testnet", chainId: TESTNET_CHAIN_IDS.SEPOLIA },
      { id: "POLYGON", name: "Polygon Amoy", fullName: "Polygon Amoy Testnet", chainId: TESTNET_CHAIN_IDS.POLYGON_AMOY },
      { id: "BASE", name: "Base Sepolia", fullName: "Base Sepolia Testnet", chainId: TESTNET_CHAIN_IDS.BASE_SEPOLIA },
    ]
    : [
      { id: "BSC", name: "BSC", fullName: "BNB Smart Chain", chainId: MAINNET_CHAIN_IDS.BSC },
      { id: "ETH", name: "Ethereum", fullName: "Ethereum", chainId: MAINNET_CHAIN_IDS.ETH },
      { id: "POLYGON", name: "Polygon", fullName: "Polygon", chainId: MAINNET_CHAIN_IDS.POLYGON },
      { id: "BASE", name: "Base", fullName: "Base", chainId: MAINNET_CHAIN_IDS.BASE },
    ]

  const currentNetwork = networks.find(n => n.chainId === chainId)

  // Contract hook
  const {
    createPresale,
    isPending,
    isConfirming,
    isSuccess,
    error: contractError,
    hash,
    creationFee,
    creationFeeUsd,
    contractAddress: presaleFactoryAddress,
    presaleAddress,
    approveTokens,
    reset: resetPresaleTx
  } = useCreateMultiCurrencyPresale(chainId)

  const {
    createFairLaunch,
    isPending: isFairLaunchPending,
    isConfirming: isFairLaunchConfirming,
    isSuccess: isFairLaunchSuccess,
    error: fairLaunchError,
    hash: fairLaunchHash,
    creationFee: fairLaunchCreationFee,
  } = useCreateFairLaunch(chainId)

  // Token info reading
  const { data: tokenName } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'name',
    query: { enabled: tokenAddress.length === 42 }
  })

  const { data: tokenSymbol } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'symbol',
    query: { enabled: tokenAddress.length === 42 }
  })

  const { data: tokenDecimals } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'decimals',
    query: { enabled: tokenAddress.length === 42 }
  })

  const { data: tokenTotalSupply } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'totalSupply',
    query: { enabled: tokenAddress.length === 42 }
  })

  const { data: tokenBalance } = useReadContract({
    address: tokenAddress as `0x${string}`,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [address as `0x${string}`],
    query: { enabled: tokenAddress.length === 42 && !!address }
  })

  // Update token info when data is loaded
  useEffect(() => {
    if (tokenName && tokenSymbol && tokenDecimals !== undefined && tokenTotalSupply) {
      setTokenInfo({
        name: tokenName,
        symbol: tokenSymbol,
        decimals: tokenDecimals,
        totalSupply: tokenTotalSupply,
        balance: tokenBalance || BigInt(0)
      })
      setTokenVerified(true)
    } else {
      setTokenInfo(null)
      setTokenVerified(false)
    }
  }, [tokenName, tokenSymbol, tokenDecimals, tokenTotalSupply, tokenBalance])

  // Save form data to sessionStorage for handleSuccess to use
  useEffect(() => {
    sessionStorage.setItem('presaleFormData', JSON.stringify({
      shortDescription,
      description,
      softCap,
      hardCap,
      minContribution,
      maxContribution,
      presaleRate,
      listingRate,
      startTime,
      endTime,
      liquidityPercent,
      website,
      twitter,
      telegram,
      youtubeLink,
      customSlug,
      uploadedLogoUrl,
      selectedDex,
      dexRouter,
      tokenomics,
      lockLiquidity,
      lpLockDays,
      isPremium,
      keyFeatures,
      auditDocUrl: auditValue,
      kycDocUrl: kycValue,
      isKYC,
      isAudit,
      allowEarlyClaim,
      referralEnabled: enableReferral,
      referralPercent,
      unsoldTokenAction,
      perCurrencyLimits,
    }))
  }, [
    shortDescription,
    description,
    softCap,
    hardCap,
    minContribution,
    maxContribution,
    presaleRate,
    listingRate,
    startTime,
    endTime,
    liquidityPercent,
    website,
    twitter,
    telegram,
    youtubeLink,
    customSlug,
    uploadedLogoUrl,
    selectedDex,
    dexRouter,
    tokenomics,
    lockLiquidity,
    lpLockDays,
    isPremium,
    keyFeatures,
    auditValue,
    kycValue,
    isKYC,
    isAudit,
    allowEarlyClaim,
    enableReferral,
    referralPercent,
    unsoldTokenAction,
    perCurrencyLimits
  ])

  // Handle success - handle approval completion or call relayer API then redirect
  useEffect(() => {
    const handleSuccess = async () => {
      if (!isSuccess || !hash) return

      // STEP 1 COMPLETE: If this was the approval step, transition to create step
      if (isApprovalStep && !isApproved) {
        setIsApproved(true)
        setIsApprovalStep(false)
        setTxStep({ current: 0, total: 0, description: '' })
        // Reset the writeContract state so isSuccess/hash clear for the next tx
        resetPresaleTx()
        return
      }

      // STEP 2: This is the create presale transaction
      // Show processing state
      setTxStep({ current: 1, total: 1, description: 'Transaction confirmed! Processing...' })

      // Check if there are pending relayer networks
      const pendingNetworksStr = sessionStorage.getItem('pendingRelayerNetworks')
      const pendingParamsStr = sessionStorage.getItem('pendingRelayerParams')

      let relayerResults: Record<string, string> = {}

      if (isRelayerMode && pendingNetworksStr && pendingParamsStr) {
        const otherNetworks = JSON.parse(pendingNetworksStr)
        const relayerParams = JSON.parse(pendingParamsStr)

        if (otherNetworks.length > 0) {
          // Keep modal open during relayer processing
          setIsRelayerProcessing(true)
          setTxStep({ current: 1, total: 1, description: 'Deploying to other networks via relayer...' })

          try {
            const res = await fetch(RELAYER_API_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ networks: otherNetworks, params: relayerParams, deployerAddress: relayerParams.deployerAddress }),
            })
            const result = await res.json()
            console.log('Relayer result:', result)

            result.results?.forEach((r: any) => {
              if (r.success && r.presaleAddress) {
                relayerResults[r.networkName] = r.presaleAddress
              }
            })
          } catch (relayerError) {
            console.error('Relayer error:', relayerError)
          }
        }

        // Clear sessionStorage
        sessionStorage.removeItem('pendingRelayerNetworks')
        sessionStorage.removeItem('pendingRelayerParams')
      }

      // Clear step indicator and close modal
      setTxStep({ current: 0, total: 0, description: '' })
      setIsRelayerProcessing(false)
      setDeploymentProgress(prev => ({ ...prev, isMultiDeploying: false, deployedAddresses: relayerResults }))

      // Build URL params
      const params = new URLSearchParams({
        tx: hash,
        chainId: String(chainId || 97),
        name: tokenInfo?.name || '',
        symbol: tokenInfo?.symbol || '',
      })

      // Add current chain deployment
      const currentNet = Object.entries(CHAIN_CONFIGS).find(([_, c]) => c.chainId === chainId)?.[0]
      const allDeployments = [
        { network: currentNet || 'Unknown', chainId: Number(chainId), txHash: hash, presaleAddress: presaleAddress || '', isCollector: false },
        ...Object.entries(relayerResults).map(([networkName, addr]) => {
          // Find chainId by network name (networkName could be "Sepolia", "Polygon Amoy", "Base Sepolia")
          const chainConfig = Object.entries(CHAIN_CONFIGS).find(([_, c]) => c.name === networkName)?.[1]
          return {
            network: networkName,
            chainId: Number(chainConfig?.chainId || 0),
            txHash: '',
            presaleAddress: addr,
            isCollector: true  // Relayer deployments are collectors on secondary chains
          }
        })
      ]

      // Save presale to database
      try {
        const savedParams = sessionStorage.getItem('presaleFormData')
        const formData = savedParams ? JSON.parse(savedParams) : {}

        await fetch('/api/presales', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            creatorWallet: address,
            name: tokenInfo?.name,
            shortDescription: formData.shortDescription,
            description: formData.description,
            logoUrl: formData.uploadedLogoUrl,
            tokenAddress,
            tokenSymbol: tokenInfo?.symbol,
            tokenDecimals: tokenInfo?.decimals,
            softCap: formData.softCap,
            hardCap: formData.hardCap,
            minBuy: formData.minContribution,
            maxBuy: formData.maxContribution,
            presaleRate: formData.presaleRate,
            listingRate: formData.listingRate,
            liquidityPercent: formData.liquidityPercent,
            startTime: formData.startTime,
            endTime: formData.endTime,
            chainId,
            website: formData.website,
            twitter: formData.twitter,
            telegram: formData.telegram,
            youtubeUrl: formData.youtubeLink,
            customSlug: formData.customSlug,
            dexName: formData.selectedDex,
            dexRouter: formData.dexRouter,
            tokenomics: JSON.stringify(formData.tokenomics),
            lockLiquidity: formData.lockLiquidity,
            lockupDays: formData.lpLockDays,
            isFeatured: formData.isPremium,
            keyFeatures: formData.keyFeatures.filter((f: string) => f.trim() !== ""),
            auditDocUrl: formData.auditDocUrl,
            kycDocUrl: formData.kycDocUrl,
            isKYC: formData.isKYC,
            isAudit: formData.isAudit,
            allowEarlyClaim: formData.allowEarlyClaim,
            referralEnabled: formData.referralEnabled,
            referralPercent: formData.referralPercent,
            unsoldTokenAction: formData.unsoldTokenAction,
            perCurrencyLimits: JSON.stringify(Object.entries(perCurrencyLimits)
              .filter(([curr, data]) => data.enabled && acceptedCurrencies[curr as keyof typeof acceptedCurrencies])
              .reduce((acc, [curr, data]) => {
                acc[curr] = { min: data.min, max: data.max };
                return acc;
              }, {} as Record<string, { min: string, max: string }>)),
            deployments: allDeployments.map(d => ({
              chainId: d.chainId,
              chainName: d.network,
              contractAddress: d.presaleAddress,
              txHash: d.txHash || null,
              status: d.presaleAddress ? 'SUCCESS' : 'FAILED',
              isCollector: d.isCollector,
            })),
          }),
        })
        console.log('[Presale] Saved to database')
      } catch (dbError) {
        console.error('[Presale] Failed to save to database:', dbError)
        // Don't block redirect if DB save fails
      }

      if (allDeployments.length > 0) {
        // Don't use encodeURIComponent - URLSearchParams.set already URL-encodes
        params.set('deployments', JSON.stringify(allDeployments))
      }

      router.push(`/create-presale/success?${params.toString()}`)
    }

    handleSuccess()
  }, [isSuccess, hash, isFairLaunchSuccess, fairLaunchHash, router, chainId, tokenInfo, presaleAddress])

  // Fetch real-time crypto prices from Binance (BNB, ETH, MATIC)
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true)
      try {
        const [bnbPrice, ethPrice, maticPrice] = await Promise.all([
          getCryptoPrice('BNB'),
          getCryptoPrice('ETH'),
          getCryptoPrice('MATIC'),
        ])
        setCryptoPrices({
          BNB: bnbPrice || 700,
          ETH: ethPrice || 3500,
          MATIC: maticPrice || 0.5,
        })
      } catch (error) {
        console.error('Failed to fetch crypto prices:', error)
      }
      setPriceLoading(false)
    }

    fetchPrices()
    // Refresh prices every 30 seconds
    const interval = setInterval(fetchPrices, 30000)
    return () => clearInterval(interval)
  }, [])

  // Network switch handler
  const handleNetworkSwitch = async (network: typeof networks[0]) => {
    if (network.chainId === chainId) return
    try {
      await switchChain({ chainId: network.chainId })
    } catch (error) {
      console.error('Failed to switch network:', error)
    }
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to server
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', 'logo')

        const res = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
        })

        const data = await res.json()
        if (data.success && data.url) {
          setUploadedLogoUrl(data.url)
          console.log('Logo uploaded:', data.url)
        }
      } catch (error) {
        console.error('Logo upload failed:', error)
      }
    }
  }

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setVideoFile(file)
    }
  }

  // Apply recommended settings - fills all presale parameters with sensible defaults
  const applyRecommendedSettings = () => {
    // Rate settings (USD mode)
    setRateType('usd')
    setPresaleRate('1000')   // 1000 tokens per $1
    setListingRate('800')    // 800 tokens per $1 (20% premium for presale buyers)

    // Caps (in USD)
    setSoftCap('50')
    setHardCap('100')

    // Contribution limits
    setMinContribution('0.01')
    setMaxContribution('5')

    // Timing: starts in 1 hour, ends in 30 days
    const now = new Date()
    const start = new Date(now.getTime() + 60 * 60 * 1000) // +1 hour
    const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000) // +30 days
    // Format as datetime-local: YYYY-MM-DDTHH:MM
    const fmt = (d: Date) => d.toISOString().slice(0, 16)
    setStartTime(fmt(start))
    setEndTime(fmt(end))

    // Liquidity & Lock
    setLiquidityPercent('60')
    setLockLiquidity(true)
    setLpLockDays('365')

    // Accepted currencies
    setAcceptedCurrencies({
      BNB: true,
      USDT: true,
      ETH: false,
      USDC: false,
      DAI: false,
    })

    // Accepted networks (just BSC by default)
    setAcceptedNetworks({
      BSC: true,
      ETH: false,
      POLYGON: false,
      BASE: false,
    })

    // Referral
    setEnableReferral(true)
    setReferralPercent('5')

    // Claim settings
    setAllowEarlyClaim(false)
    setUnsoldTokenAction('burn')

    // Not fair launch, not premium by default
    setIsFairLaunch(false)
    setIsPremium(false)

    // Token amount: use 50% of balance if token is verified, otherwise a sensible default
    if (tokenInfo && tokenInfo.balance > BigInt(0)) {
      const halfBalance = Number(tokenInfo.balance) / (10 ** tokenInfo.decimals) / 2
      setTokenAmount(String(Math.floor(halfBalance)))
    } else {
      setTokenAmount('100000') // Default fallback
    }
  }

  const handleLaunchPresale = async () => {
    setDeployError(null)

    if (!isConnected || !address) {
      setDeployError("Please connect your wallet first")
      return
    }

    if (!tokenVerified || !tokenInfo) {
      setDeployError("Please verify your token first")
      return
    }

    // Get selected networks
    const selectedNetworks = Object.entries(acceptedNetworks)
      .filter(([_, selected]) => selected)
      .map(([network]) => network)
      .filter(network => CHAIN_CONFIGS[network])

    if (selectedNetworks.length === 0) {
      setDeployError("Please select at least one network")
      return
    }

    // Validate token amount
    if (!tokenAmount || parseFloat(tokenAmount) <= 0) {
      setDeployError("Please enter the amount of tokens to sell")
      return
    }

    // Validate rates
    if (!presaleRate || parseFloat(presaleRate) <= 0) {
      setDeployError("Please enter a valid Presale Rate")
      return
    }

    if (!listingRate || parseFloat(listingRate) <= 0) {
      setDeployError("Please enter a valid Listing Rate")
      return
    }

    try {
      // Parse token amount to deposit (in wei, based on token decimals)
      // Use string manipulation to avoid floating-point precision issues with large numbers
      const decimals = tokenInfo?.decimals || 18
      const amountStr = tokenAmount || "0"
      const [whole, fraction = ""] = amountStr.split(".")
      const paddedFraction = fraction.padEnd(decimals, "0").slice(0, decimals)
      const tokenAmountWei = BigInt(whole + paddedFraction)

      // Check balance
      if (tokenInfo && tokenAmountWei > tokenInfo.balance) {
        setDeployError(`Insufficient token balance. You have ${(Number(tokenInfo.balance) / (10 ** decimals)).toLocaleString()} ${tokenInfo.symbol}`)
        return
      }

      // STEP 1: Approve tokens (if not already approved)
      if (isApprovalStep && !isApproved) {
        // Determine primary target network (first selected network)
        const primaryNetwork = selectedNetworks[0]
        const targetChainConfig = CHAIN_CONFIGS[primaryNetwork]

        // Switch to target network BEFORE approval
        if (targetChainConfig && chainId !== targetChainConfig.chainId) {
          setTxStep({ current: 1, total: 3, description: `Switching to ${targetChainConfig.name}...` })
          await switchChain({ chainId: targetChainConfig.chainId })
          // After switch, user needs to click button again
          setTxStep({ current: 0, total: 0, description: '' })
          return
        }

        setTxStep({ current: 1, total: 2, description: 'Step 1: Approve tokens for presale contract' })
        await approveTokens(tokenAddress, tokenAmountWei)
        // After approval tx is submitted, we wait for it to confirm
        // The isSuccess hook will handle the state transition
        return
      }

      const soft = parseEther(softCap)
      const hard = parseEther(hardCap)
      const min = parseEther(minContribution)
      const max = parseEther(maxContribution)
      const start = Math.floor(new Date(startTime).getTime() / 1000)
      const end = Math.floor(new Date(endTime).getTime() / 1000)
      const liquidity = parseInt(liquidityPercent)

      // Build payment tokens array
      // rateType determines how presaleRate is interpreted:
      // - 'usd': presaleRate = tokens per $1 (stablecoins use directly, native uses price)
      // - 'native': presaleRate = tokens per 1 native currency (BNB/ETH/MATIC)
      const paymentTokens: PaymentToken[] = []
      const inputRate = parseFloat(presaleRate)

      if (acceptedCurrencies.BNB) {
        let bnbRate: bigint
        if (rateType === 'usd') {
          // User entered tokens per $1, multiply by BNB price
          bnbRate = BigInt(Math.floor(inputRate * cryptoPrices.BNB * 1e18))
        } else {
          // User entered tokens per 1 BNB directly  
          bnbRate = parseEther(presaleRate)
        }
        paymentTokens.push({ address: PAYMENT_TOKENS.NATIVE, rate: bnbRate })
      }

      // Stablecoins always use tokens per $1
      const stablecoinRate = rateType === 'usd'
        ? parseEther(presaleRate)
        : parseEther((inputRate / cryptoPrices.BNB).toFixed(18)) // Convert from native to USD

      if (acceptedCurrencies.USDT) {
        paymentTokens.push({
          address: isTestnet ? PAYMENT_TOKENS.USDT_BSC_TESTNET : PAYMENT_TOKENS.USDT_BSC,
          rate: stablecoinRate,
        })
      }
      if (acceptedCurrencies.USDC) {
        paymentTokens.push({
          address: isTestnet ? PAYMENT_TOKENS.USDC_BSC_TESTNET : PAYMENT_TOKENS.USDC_BSC,
          rate: stablecoinRate,
        })
      }
      if (acceptedCurrencies.DAI) {
        paymentTokens.push({ address: PAYMENT_TOKENS.DAI_BSC, rate: stablecoinRate })
      }

      if (paymentTokens.length === 0) {
        setDeployError("Please select at least one payment currency")
        return
      }

      // Calculate fee based on tier (standard vs premium)
      const presaleBaseFee = process.env.NEXT_PUBLIC_PRESALE_CREATION_FEE;
      const presalePremiumFee = process.env.NEXT_PUBLIC_PRESALE_PREMIUM_FEE;

      if (!presaleBaseFee) {
        throw new Error("Configuration Error: NEXT_PUBLIC_PRESALE_CREATION_FEE is not set in .env");
      }

      const standardFeeAmount = parseEther(presaleBaseFee)
      const premiumFeeAmount = parseEther(presalePremiumFee || '0')
      const totalFee = isPremium ? standardFeeAmount + premiumFeeAmount : standardFeeAmount

      // MULTI-CHAIN DEPLOYMENT
      if (selectedNetworks.length === 1) {
        // Single chain deployment
        const targetChain = CHAIN_CONFIGS[selectedNetworks[0]]
        if (targetChain && chainId !== targetChain.chainId) {
          setTxStep({ current: 1, total: 2, description: 'Switching Network' })
          await switchChain({ chainId: targetChain.chainId })
        }
        setTxStep({ current: 1, total: 1, description: 'Creating Presale Contract' })

        // Save to database
        try {
          await fetch('/api/presales', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: tokenInfo?.name || "New Presale",
              symbol: tokenInfo?.symbol || "TKN",
              description: description || "",
              shortDescription: shortDescription || "",
              logoUrl: uploadedLogoUrl || "",
              tokenAddress,
              tokenDecimals: tokenInfo?.decimals || 18,
              softCap, hardCap, presaleRate,
              listingRate: listingRate || presaleRate,
              liquidityPercent: liquidity,
              startTime, endTime, minBuy: minContribution, maxBuy: maxContribution,
              chainId, dexName: selectedDex, dexRouter,
              isFeatured: isPremium,
              keyFeatures: keyFeatures.filter(f => f.trim() !== ""),
              auditDocUrl: auditValue, kycDocUrl: kycValue, isKYC, isAudit,
              tokenomics: JSON.stringify(tokenomics),
              lockLiquidity, lockupDays: parseInt(lpLockDays),
              allowEarlyClaim,
              referralEnabled: enableReferral,
              referralPercent,
              unsoldTokenAction,
            }),
          })
        } catch (dbErr) {
          console.error("Database save failed:", dbErr)
        }

        if (isFairLaunch) {
          await createFairLaunch({
            tokenAddress,
            totalTokens: BigInt(tokenAmountWei),
            softCap: soft,
            minContribution: min,
            maxContribution: max,
            startTime: start,
            endTime: end,
            liquidityPercent: liquidity,
            paymentTokens: paymentTokens.map(t => t.address),
            rates: paymentTokens.map(t => t.rate),
          })
        } else {
          await createPresale({
            tokenAddress, tokenAmount: tokenAmountWei, softCap: soft, hardCap: hard, minContribution: min,
            maxContribution: max, startTime: start, endTime: end,
            liquidityPercent: liquidity, paymentTokens, customFee: totalFee,
            allowEarlyClaim: allowEarlyClaim,
            referralPercent: enableReferral ? BigInt(referralPercent) : BigInt(0),
            lpLockDuration: lockLiquidity ? BigInt(parseInt(lpLockDays) * 86400) : BigInt(0),
            tokenLocker: getTokenLockerAddress(chainId) || '0x0000000000000000000000000000000000000000',
            dexRouter: dexRouter || '0x0000000000000000000000000000000000000000',
          })
        }
        setTxStep({ current: 0, total: 0, description: '' })
      } else if (isRelayerMode) {
        // RELAYER MODE: User approves 1 transaction, backend handles rest
        const currentNet = Object.entries(CHAIN_CONFIGS).find(([_, c]) => c.chainId === chainId)?.[0] || selectedNetworks[0]
        const otherNetworks = selectedNetworks.filter(n => n !== currentNet)

        setDeploymentProgress({
          currentNetwork: currentNet,
          completedNetworks: [],
          deployedAddresses: {},
          isMultiDeploying: true
        })

        // User only sees 1 step - their transaction
        setTxStep({ current: 1, total: 1, description: `Creating presale on ${CHAIN_CONFIGS[currentNet]?.name || currentNet}` })

        // Store other networks info for after transaction completes
        // The useEffect watching isSuccess will handle relayer call
        sessionStorage.setItem('pendingRelayerNetworks', JSON.stringify(otherNetworks))
        sessionStorage.setItem('pendingRelayerParams', JSON.stringify({
          saleToken: tokenAddress,
          softCap: soft.toString(),
          hardCap: hard.toString(),
          minContribution: min.toString(),
          maxContribution: max.toString(),
          startTime: start,
          endTime: end,
          liquidityPercent: liquidity,
          paymentTokens: paymentTokens.map(t => t.address),
          rates: paymentTokens.map(t => t.rate.toString()),
          deployerAddress: address,
          // Cross-chain linking params
          tokenName: tokenInfo?.name || 'Token',
          tokenSymbol: tokenInfo?.symbol || 'TKN',
          primaryChainId: chainId,
          allowEarlyClaim: allowEarlyClaim,
          referralPercent: enableReferral ? referralPercent : "0",
          // presaleId will be generated by relayer based on saleToken + startTime
          perCurrencyLimits: Object.entries(perCurrencyLimits)
            .filter(([curr, data]) => data.enabled && acceptedCurrencies[curr as keyof typeof acceptedCurrencies])
            .reduce((acc, [curr, data]) => {
              acc[curr] = { min: data.min, max: data.max };
              return acc;
            }, {} as Record<string, { min: string, max: string }>)
        }))

        // Submit transaction - user signs once
        if (isFairLaunch) {
          await createFairLaunch({
            tokenAddress,
            totalTokens: BigInt(tokenAmountWei),
            softCap: soft,
            minContribution: min,
            maxContribution: max,
            startTime: start,
            endTime: end,
            liquidityPercent: liquidity,
            paymentTokens: paymentTokens.map(t => t.address),
            rates: paymentTokens.map(t => t.rate),
          })
        } else {
          await createPresale({
            tokenAddress, tokenAmount: tokenAmountWei, softCap: soft, hardCap: hard, minContribution: min,
            maxContribution: max, startTime: start, endTime: end,
            liquidityPercent: liquidity, paymentTokens, customFee: totalFee,
            allowEarlyClaim: allowEarlyClaim,
            referralPercent: enableReferral ? BigInt(referralPercent) : BigInt(0),
            lpLockDuration: lockLiquidity ? BigInt(parseInt(lpLockDays) * 86400) : BigInt(0),
            tokenLocker: getTokenLockerAddress(chainId) || '0x0000000000000000000000000000000000000000',
            dexRouter: dexRouter || '0x0000000000000000000000000000000000000000',
          })
        }

        // Note: After this, the modal will show "Confirming Transaction"
        // When isSuccess becomes true, the useEffect will handle relayer + redirect
      } else {
        // SEQUENTIAL MODE: Frontend chain switching
        setDeploymentProgress({ currentNetwork: null, completedNetworks: [], deployedAddresses: {}, isMultiDeploying: true })

        for (const networkKey of selectedNetworks) {
          const targetChain = CHAIN_CONFIGS[networkKey]
          if (!targetChain?.factoryAddress) continue

          setDeploymentProgress(prev => ({ ...prev, currentNetwork: networkKey }))

          if (chainId !== targetChain.chainId) {
            try {
              await switchChain({ chainId: targetChain.chainId })
              await new Promise(r => setTimeout(r, 2000))
            } catch { continue }
          }

          try {
            if (isFairLaunch) {
              await createFairLaunch({
                tokenAddress,
                totalTokens: BigInt(tokenAmountWei),
                softCap: soft,
                minContribution: min,
                maxContribution: max,
                startTime: start,
                endTime: end,
                liquidityPercent: liquidity,
                paymentTokens: paymentTokens.map(t => t.address),
                rates: paymentTokens.map(t => t.rate),
              })
            } else {
              await createPresale({
                tokenAddress, tokenAmount: tokenAmountWei, softCap: soft, hardCap: hard, minContribution: min,
                maxContribution: max, startTime: start, endTime: end,
                liquidityPercent: liquidity, paymentTokens, customFee: totalFee,
                allowEarlyClaim: allowEarlyClaim,
                referralPercent: enableReferral ? BigInt(referralPercent) : BigInt(0),
                lpLockDuration: lockLiquidity ? BigInt(parseInt(lpLockDays) * 86400) : BigInt(0),
                tokenLocker: getTokenLockerAddress(chainId) || '0x0000000000000000000000000000000000000000',
                dexRouter: dexRouter || '0x0000000000000000000000000000000000000000',
              })
            }
            const presaleAddr = presaleAddress // This might not be updated yet due to hook behavior
            console.log(`Deployed on ${networkKey} at ${presaleAddr}`)

            // Optional: Set limits if enabled (this would require another tx, better to handle in relayer or factory)
            // For now, relayer is the primary multi-chain path where we should ensure this is handled.

            await new Promise(r => setTimeout(r, 3000))
            setDeploymentProgress(prev => ({ ...prev, completedNetworks: [...prev.completedNetworks, networkKey] }))
          } catch (e) { console.error(`Deploy failed on ${networkKey}:`, e) }
        }
        setDeploymentProgress(prev => ({ ...prev, currentNetwork: null, isMultiDeploying: false }))
      }
    } catch (err: any) {
      setDeployError(err.message || "Failed to create presale")
      setDeploymentProgress(prev => ({ ...prev, isMultiDeploying: false }))
    }
  }

  // Fee display
  const feeDisplay = creationFee ? parseFloat(formatEther(creationFee)).toFixed(4) : '...'
  const feeUsdDisplay = creationFeeUsd ? `~$${creationFeeUsd.toFixed(0)} USD` : ''

  const steps = [
    { id: 0, name: "Verify Token", icon: CheckCircle2 },
    { id: 1, name: "Presale Info", icon: Coins },
    { id: 2, name: "Add Details", icon: Upload },
    { id: 3, name: "Review", icon: Rocket },
  ]

  return (
    <div className="min-h-screen flex flex-col pt-20">
      <Header />

      <main className="flex-1 py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="space-y-4 mb-12 text-center">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Create Token Presale</h1>
              <p className="text-lg text-muted-foreground text-balance">
                Launch your token presale in minutes. No coding required.
              </p>
            </div>

            {/* Steps Progress */}
            <div className="mb-12 flex justify-center">
              <div className="flex items-center w-full max-w-3xl px-4">
                {steps.map((step, index) => {
                  const Icon = step.icon
                  const isActive = index === activeStep
                  const isCompleted = index < activeStep

                  return (
                    <div key={index} className="flex items-center flex-1">
                      <div className="flex flex-col items-center gap-2 w-full">
                        <div
                          className={`flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full border-2 transition-colors ${isActive
                            ? "border-primary bg-primary text-primary-foreground"
                            : isCompleted
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-muted-foreground/25 text-muted-foreground"
                            }`}
                        >
                          <Icon className="h-4 w-4 md:h-5 md:w-5" />
                        </div>
                        <span
                          className={`text-xs md:text-sm font-medium text-center ${isActive || isCompleted ? "text-foreground" : "text-muted-foreground"
                            }`}
                        >
                          {step.name}
                        </span>
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`h-0.5 flex-1 mx-2 md:mx-4 ${isCompleted ? "bg-primary" : "bg-muted-foreground/25"}`}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Form */}
            <Card className="p-6 md:p-8">
              <Tabs value={`step-${activeStep}`} className="space-y-8">
                <TabsContent value="step-0" className="space-y-6 mt-0">
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold">Verify Token</h2>
                    <p className="text-muted-foreground">
                      Enter your token address to verify and import token information.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="token-network">Network *</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full justify-between bg-transparent" disabled={isSwitchingNetwork}>
                            <span>{isSwitchingNetwork ? "Switching..." : (currentNetwork?.fullName || "Select Network")}</span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          {networks.map((network) => (
                            <DropdownMenuItem
                              key={network.id}
                              onClick={() => handleNetworkSwitch(network)}
                              className={chainId === network.chainId ? "bg-primary/10" : ""}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{network.fullName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {chainId === network.chainId ? "✓ Connected" : network.name}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {!isConnected && (
                        <p className="text-xs text-muted-foreground">Connect wallet to switch networks</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="token-address">Token Address *</Label>
                      <Input
                        id="token-address"
                        placeholder="0x..."
                        className="font-mono"
                        value={tokenAddress || ""}
                        onChange={(e) => setTokenAddress(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Enter the token contract address on {currentNetwork?.fullName || "selected network"}
                      </p>
                    </div>

                    {/* Token Info Display */}
                    {tokenAddress.length === 42 && (
                      <div className={`p-4 rounded-lg border ${tokenVerified ? 'border-[#7ed957]/40 bg-[#7ed957]/5' : 'border-yellow-500/40 bg-yellow-500/5'}`}>
                        {tokenVerified && tokenInfo ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-5 w-5 text-[#7ed957]" />
                              <span className="font-semibold text-[#7ed957]">Token Verified</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Name</p>
                                <p className="font-medium">{tokenInfo.name}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Symbol</p>
                                <p className="font-medium">{tokenInfo.symbol}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Decimals</p>
                                <p className="font-medium">{tokenInfo.decimals}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Total Supply</p>
                                <p className="font-medium">{formatUnits(tokenInfo.totalSupply, tokenInfo.decimals)}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-muted-foreground">Your Balance</p>
                                <p className="font-medium">{formatUnits(tokenInfo.balance, tokenInfo.decimals)} {tokenInfo.symbol}</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-yellow-500" />
                            <span className="text-yellow-500">Fetching token info...</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="p-4 rounded-lg bg-muted/50 border border-border/40">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Requirements</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li>• Token must be deployed on {currentNetwork?.fullName || "the selected network"}</li>
                            <li>• You must own tokens to allocate for presale</li>
                            <li>• You will need to approve tokens for the presale contract</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="step-1" className="space-y-6 mt-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="space-y-1">
                      <h2 className="text-2xl font-bold">Presale Information</h2>
                      <p className="text-muted-foreground">Configure your presale parameters and pricing.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={applyRecommendedSettings}
                      className="gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary/60 transition-all shrink-0"
                    >
                      <Star className="h-4 w-4 text-primary" />
                      Recommended Settings
                    </Button>
                  </div>

                  <div className="grid gap-6">
                    {/* Fair Launch Toggle */}
                    <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="fair-launch"
                          checked={isFairLaunch}
                          onCheckedChange={(checked) => setIsFairLaunch(checked === true)}
                          className="mt-1"
                        />
                        <div className="space-y-2 flex-1">
                          <label htmlFor="fair-launch" className="text-base font-semibold cursor-pointer">
                            Fair Launch Mode
                          </label>
                          <p className="text-sm text-muted-foreground">
                            Enable fair launch for equal opportunity. Final price determined by total contributions.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Token Info Banner */}
                    {tokenInfo && (
                      <div className="p-4 rounded-lg bg-muted/50 border border-border/40">
                        <div className="flex items-center gap-3">
                          <Icons8Icon name="coins" size="lg" />
                          <div>
                            <p className="font-medium">{tokenInfo.name} ({tokenInfo.symbol})</p>
                            <p className="text-xs text-muted-foreground">Your balance: {formatUnits(tokenInfo.balance, tokenInfo.decimals)} {tokenInfo.symbol}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Accepted Currencies */}
                    <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                      <div className="space-y-2">
                        <h3 className="font-semibold">Accepted Payment Currencies</h3>
                        <p className="text-sm text-muted-foreground">
                          Select which cryptocurrencies users can use to participate
                        </p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.keys(acceptedCurrencies).map((currency) => (
                          <div key={currency} className="space-y-3 p-3 rounded-lg border bg-background/50">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`currency-${currency}`}
                                checked={acceptedCurrencies[currency as keyof typeof acceptedCurrencies]}
                                onCheckedChange={(checked) =>
                                  setAcceptedCurrencies((prev) => ({ ...prev, [currency]: checked === true }))
                                }
                              />
                              <label
                                htmlFor={`currency-${currency}`}
                                className="text-sm font-bold cursor-pointer"
                              >
                                {currency}
                              </label>
                            </div>

                            {acceptedCurrencies[currency as keyof typeof acceptedCurrencies] && (
                              <div className="space-y-2 pt-2 border-t border-border/40 animate-in fade-in slide-in-from-top-1 duration-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Custom Limits</span>
                                  <Switch
                                    checked={perCurrencyLimits[currency]?.enabled || false}
                                    onCheckedChange={(checked) => setPerCurrencyLimits(prev => ({
                                      ...prev,
                                      [currency]: { ...prev[currency], enabled: checked }
                                    }))}
                                    className="scale-75"
                                  />
                                </div>
                                {perCurrencyLimits[currency]?.enabled && (
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-[9px] uppercase">Min</Label>
                                      <Input
                                        className="h-7 text-xs px-2"
                                        placeholder="Min"
                                        value={perCurrencyLimits[currency].min}
                                        onChange={(e) => setPerCurrencyLimits(prev => ({
                                          ...prev,
                                          [currency]: { ...prev[currency], min: e.target.value }
                                        }))}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-[9px] uppercase">Max</Label>
                                      <Input
                                        className="h-7 text-xs px-2"
                                        placeholder="Max"
                                        value={perCurrencyLimits[currency].max}
                                        onChange={(e) => setPerCurrencyLimits(prev => ({
                                          ...prev,
                                          [currency]: { ...prev[currency], max: e.target.value }
                                        }))}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Accepted Networks */}
                    <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                      <div className="space-y-2">
                        <h3 className="font-semibold">Supported Networks</h3>
                        <p className="text-sm text-muted-foreground">
                          Select which blockchain networks will be supported for this presale
                        </p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.keys(acceptedNetworks).map((network) => (
                          <div key={network} className="flex items-center space-x-2">
                            <Checkbox
                              id={`network-${network}`}
                              checked={acceptedNetworks[network as keyof typeof acceptedNetworks]}
                              onCheckedChange={(checked) =>
                                setAcceptedNetworks((prev) => ({ ...prev, [network]: checked === true }))
                              }
                            />
                            <label
                              htmlFor={`network-${network}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {network}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Rate Type Toggle & Live Prices */}
                    <div className="p-4 bg-muted/50 rounded-lg border border-border/50 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium">Rate Input Mode</Label>
                        <div className="flex gap-2">
                          <Button
                            variant={rateType === 'usd' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setRateType('usd')}
                          >
                            Per $1 USD
                          </Button>
                          <Button
                            variant={rateType === 'native' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setRateType('native')}
                          >
                            Per Native Token
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <span className="text-yellow-400">⬡</span> BNB: ${cryptoPrices.BNB?.toFixed(2) || '700'}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="text-blue-400">⟠</span> ETH: ${cryptoPrices.ETH?.toFixed(2) || '3500'}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="text-purple-400">⬢</span> MATIC: ${cryptoPrices.MATIC?.toFixed(2) || '0.50'}
                        </span>
                        {priceLoading && <span className="text-primary animate-pulse">Updating...</span>}
                      </div>
                    </div>

                    {/* Presale Settings */}
                    {!isFairLaunch ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="presale-rate">Presale Rate *</Label>
                          <Input
                            id="presale-rate"
                            type="number"
                            placeholder={rateType === 'usd' ? '1000' : String(Math.round(1000 * cryptoPrices.BNB))}
                            value={presaleRate || ""}
                            onChange={(e) => setPresaleRate(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            {rateType === 'usd'
                              ? 'Tokens per $1 USD during presale'
                              : `Tokens per 1 ${isTestnet ? 'tBNB' : 'BNB'} during presale`}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="listing-rate">Listing Rate *</Label>
                          <Input
                            id="listing-rate"
                            type="number"
                            placeholder={presaleRate || (rateType === 'usd' ? '900' : '90000')}
                            value={listingRate || ""}
                            onChange={(e) => setListingRate(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            {rateType === 'usd'
                              ? 'Tokens per $1 USD on DEX listing'
                              : `Tokens per 1 ${isTestnet ? 'tBNB' : 'BNB'} on DEX listing`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="total-tokens">Total Tokens for Sale *</Label>
                          <Input
                            id="total-tokens"
                            type="number"
                            placeholder="1,000,000"
                            value={tokenAmount || ""}
                            onChange={(e) => setTokenAmount(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            Total volume of tokens to be distributed in this Fair Launch
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="listing-benchmark">Listing Rate Comparison (%) *</Label>
                          <Input
                            id="listing-benchmark"
                            type="number"
                            placeholder="100"
                            value={listingRate || "100"}
                            onChange={(e) => setListingRate(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            Listing price relative to fair launch final price (e.g., 100% = same price)
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Caps */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="soft-cap">Soft Cap ({rateType === 'usd' ? 'USD' : (isTestnet ? 'tBNB' : 'BNB')}) *</Label>
                        <Input
                          id="soft-cap"
                          type="number"
                          placeholder="50"
                          value={softCap || ""}
                          onChange={(e) => setSoftCap(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Minimum to consider presale successful</p>
                      </div>

                      {!isFairLaunch && (
                        <div className="space-y-2">
                          <Label htmlFor="hard-cap">Hard Cap ({rateType === 'usd' ? 'USD' : (isTestnet ? 'tBNB' : 'BNB')}) *</Label>
                          <Input
                            id="hard-cap"
                            type="number"
                            placeholder="100"
                            value={hardCap || ""}
                            onChange={(e) => setHardCap(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">Maximum raise limit</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="liquidity">Liquidity (%) *</Label>
                      <Input
                        id="liquidity"
                        type="number"
                        placeholder="60"
                        value={liquidityPercent}
                        onChange={(e) => setLiquidityPercent(e.target.value)}
                        min="51" max="100"
                      />
                      <p className="text-xs text-muted-foreground">% of raised funds for liquidity pool</p>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="dex">Listing Exchange *</Label>
                        <select
                          id="dex"
                          className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                          value={selectedDex}
                          onChange={(e) => {
                            setSelectedDex(e.target.value)
                            if (e.target.value !== 'custom') {
                              const router = getDexRouter(e.target.value)
                              setDexRouter(router || "")
                            } else {
                              setDexRouter("")
                            }
                          }}
                        >
                          <option value="pancakeswap-v2">🥞 PancakeSwap V2 (BSC)</option>
                          <option value="pancakeswap-v3">🥞 PancakeSwap V3 (BSC)</option>
                          <option value="uniswap">🦄 Uniswap (ETH)</option>
                          <option value="sushiswap">🍣 SushiSwap (Multi)</option>
                          <option value="quickswap">⚡ QuickSwap (Polygon)</option>
                          <option value="baseswap">🔵 BaseSwap (Base)</option>
                          <option value="custom">⚙️ Custom Router</option>
                        </select>
                        <p className="text-xs text-muted-foreground">The DEX where liquidity will be added after successful presale</p>
                      </div>

                      {selectedDex === 'custom' && (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                          <Label htmlFor="dex-router">Custom Router Address *</Label>
                          <Input
                            id="dex-router"
                            placeholder="0x..."
                            value={dexRouter || ""}
                            onChange={(e) => setDexRouter(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">Enter the DEX router contract address (e.g. PancakeRouter V2 address)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* LP Locking Options */}
                  <div className="p-4 bg-gradient-to-r from-yellow-500/5 to-transparent rounded-lg border border-yellow-500/20">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                          <span className="text-xl">🔒</span>
                        </div>
                        <div>
                          <Label className="text-base font-medium">Lock LP Tokens</Label>
                          <p className="text-xs text-muted-foreground">Lock liquidity to build investor trust</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={lockLiquidity}
                          onChange={(e) => setLockLiquidity(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-muted peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7ed957]"></div>
                      </label>
                    </div>

                    {lockLiquidity && (
                      <div className="space-y-2 pt-3 border-t border-border/50">
                        <Label htmlFor="lock-duration">Lock Duration</Label>
                        <select
                          id="lock-duration"
                          className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                          value={lpLockDays}
                          onChange={(e) => setLpLockDays(e.target.value)}
                        >
                          <option value="90">90 Days (3 months)</option>
                          <option value="180">180 Days (6 months)</option>
                          <option value="365">365 Days (1 year) - Recommended</option>
                          <option value="730">730 Days (2 years)</option>
                          <option value="9999">Forever (Permanent Lock)</option>
                        </select>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <span className="text-[#7ed957]">✓</span>
                          Locked LP tokens build trust and prevent rug pulls
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Claim Settings */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                    <h4 className="font-medium flex items-center gap-2">
                      <Icons8Icon name="gift-fluent" size="md" />
                      Claim Settings
                    </h4>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Allow Early Claim</p>
                        <p className="text-xs text-muted-foreground">Users can claim tokens during presale (before finalization)</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={allowEarlyClaim}
                          onChange={(e) => setAllowEarlyClaim(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-muted peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#7ed957]"></div>
                      </label>
                    </div>

                    <div className="space-y-2 pt-3 border-t border-border/50">
                      <Label>Unsold Tokens</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="unsoldTokens"
                            value="refund"
                            checked={unsoldTokenAction === "refund"}
                            onChange={() => setUnsoldTokenAction("refund")}
                            className="w-4 h-4 text-primary"
                          />
                          <span className="text-sm">Refund to Owner</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="unsoldTokens"
                            value="burn"
                            checked={unsoldTokenAction === "burn"}
                            onChange={() => setUnsoldTokenAction("burn")}
                            className="w-4 h-4 text-primary"
                          />
                          <span className="text-sm">Burn</span>
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {unsoldTokenAction === "burn"
                          ? "Unsold tokens will be permanently burned after presale ends"
                          : "Unsold tokens will be returned to the presale creator"
                        }
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="token-amount" className="text-base font-medium">
                        Tokens to Sell *
                      </Label>
                      {tokenInfo && (
                        <span className="text-sm text-muted-foreground">
                          Balance: {(Number(tokenInfo.balance) / (10 ** tokenInfo.decimals)).toLocaleString()} {tokenInfo.symbol}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="token-amount"
                        type="number"
                        placeholder="1000000"
                        value={tokenAmount}
                        onChange={(e) => setTokenAmount(e.target.value)}
                        className="flex-1"
                      />
                      {tokenInfo && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTokenAmount(String(Number(tokenInfo.balance) / (10 ** tokenInfo.decimals)))}
                        >
                          MAX
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Amount of {tokenInfo?.symbol || 'tokens'} you will deposit to the presale contract for buyers to purchase.
                      <br />
                      <span className="text-yellow-500">⚠️ You will need to approve these tokens before creating the presale.</span>
                    </p>
                  </div>

                  {/* Creation Fee Display */}
                  <div className="p-4 bg-gradient-to-r from-[#7ed957]/10 to-[#7ed957]/10 rounded-lg border border-[#7ed957]/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#7ed957]">🚀 Creation Fee</span>
                      <span className="text-lg font-bold text-[#7ed957]">
                        {feeDisplay} {isTestnet ? 'tBNB' : 'BNB'}
                        {feeUsdDisplay && <span className="text-xs font-normal text-muted-foreground ml-1">({feeUsdDisplay})</span>}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This fee is paid when creating the presale contract. It covers deployment costs and platform fees.
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-[#7ed957]/20 text-[#7ed957] rounded">Step 1: Approve Tokens (Free)</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="px-2 py-1 bg-[#7ed957]/20 text-[#7ed957] rounded">Step 2: Create Presale ({feeDisplay} {isTestnet ? 'tBNB' : 'BNB'})</span>
                    </div>
                  </div>


                  {/* Global Min/Max removed - use per-currency limits below */}


                  {/* Timing */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-time">Start Time *</Label>
                      <Input
                        id="start-time"
                        type="datetime-local"
                        value={startTime || ""}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end-time">End Time *</Label>
                      <Input
                        id="end-time"
                        type="datetime-local"
                        value={endTime || ""}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                </TabsContent>



                <TabsContent value="step-2" className="space-y-6 mt-0">
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold">Additional Details</h2>
                    <p className="text-muted-foreground">Add project information to attract investors.</p>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="logo-upload">Project Logo *</Label>
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="relative">
                            <Input
                              id="logo-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="cursor-pointer"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Recommended: 200x200px, PNG or JPG, max 2MB
                          </p>
                        </div>
                        {logoPreview && (
                          <div className="relative w-20 h-20 rounded-lg border bg-muted/30 overflow-hidden">
                            <img
                              src={logoPreview || "/placeholder.svg"}
                              alt="Logo preview"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => setLogoPreview(null)}
                              className="absolute top-1 right-1 p-1 bg-background/80 rounded-full hover:bg-background"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Project Video (Optional)</Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={videoType === "upload" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setVideoType("upload")}
                            className="gap-2"
                          >
                            <Upload className="h-4 w-4" />
                            Upload Video
                          </Button>
                          <Button
                            type="button"
                            variant={videoType === "youtube" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setVideoType("youtube")}
                            className="gap-2"
                          >
                            <Youtube className="h-4 w-4" />
                            YouTube Link
                          </Button>
                        </div>
                      </div>

                      {videoType === "upload" ? (
                        <div className="space-y-2">
                          <Input
                            key="video-upload-input"
                            id="video-upload"
                            type="file"
                            accept="video/*"
                            onChange={handleVideoUpload}
                            className="cursor-pointer"
                          />
                          {videoFile && (
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                              <span className="text-sm truncate">{videoFile.name}</span>
                              <button
                                type="button"
                                onClick={() => setVideoFile(null)}
                                className="p-1 hover:bg-background rounded"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">Max 50MB, MP4 or MOV format recommended</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Input
                            key="youtube-link-input"
                            id="youtube-link"
                            type="url"
                            placeholder="https://www.youtube.com/watch?v=..."
                            value={youtubeLink || ""}
                            onChange={(e) => setYoutubeLink(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">Enter a valid YouTube video URL</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        placeholder="https://yourproject.com"
                        value={website || ""}
                        onChange={(e) => setWebsite(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="custom-slug">Custom Presale URL (Optional)</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">/presale/</span>
                        <Input
                          id="custom-slug"
                          placeholder="nifi-token"
                          value={customSlug || ""}
                          onChange={(e) => setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Only lowercase letters, numbers, and hyphens. Example: "nifi-token", "ninjafusion"
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="twitter">Twitter</Label>
                        <Input
                          id="twitter"
                          placeholder="@yourproject"
                          value={twitter || ""}
                          onChange={(e) => setTwitter(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="telegram">Telegram</Label>
                        <Input
                          id="telegram"
                          placeholder="@yourproject"
                          value={telegram || ""}
                          onChange={(e) => setTelegram(e.target.value)}
                        />
                      </div>
                    </div>



                    {/* Key Features Section */}
                    <div className="space-y-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-base">Key Features (Points)</Label>
                          <p className="text-xs text-muted-foreground">Highlight up to 10 key selling points of your project</p>
                        </div>
                        <Badge variant="outline">{keyFeatures.length}/10</Badge>
                      </div>
                      <div className="grid gap-2">
                        {keyFeatures.map((feature, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              placeholder={`Feature #${idx + 1}`}
                              value={feature}
                              onChange={(e) => {
                                const updated = [...keyFeatures]
                                updated[idx] = e.target.value
                                setKeyFeatures(updated)
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              type="button"
                              onClick={() => setKeyFeatures(keyFeatures.filter((_, i) => i !== idx))}
                              disabled={keyFeatures.length === 1}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                        {keyFeatures.length < 10 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full mt-1 border-dashed"
                            onClick={() => setKeyFeatures([...keyFeatures, ""])}
                          >
                            + Add Another Feature
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Trust & Security Section */}
                    <div className="space-y-6 pt-4 border-t">
                      <div className="space-y-0.5">
                        <Label className="text-base text-primary">Trust & Security (Optional)</Label>
                        <p className="text-xs text-muted-foreground">Verification builds trust with investors</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Audit Proof */}
                        <div className="space-y-4 p-4 rounded-lg bg-muted/30 border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icons8Icon name="shield" size="sm" />
                              <Label className="font-semibold">Audit Status</Label>
                            </div>
                            <Switch checked={isAudit} onCheckedChange={setIsAudit} />
                          </div>

                          {isAudit && (
                            <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                              <div className="flex rounded-md bg-background p-0.5 border">
                                <button
                                  type="button"
                                  className={`flex-1 py-1 text-xs rounded-sm transition-all ${auditType === 'link' ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted'}`}
                                  onClick={() => setAuditType('link')}
                                >
                                  Link
                                </button>
                                <button
                                  type="button"
                                  className={`flex-1 py-1 text-xs rounded-sm transition-all ${auditType === 'upload' ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted'}`}
                                  onClick={() => setAuditType('upload')}
                                >
                                  Upload
                                </button>
                              </div>

                              {auditType === 'link' ? (
                                <Input
                                  placeholder="URL to Audit Report"
                                  value={auditValue}
                                  onChange={(e) => setAuditValue(e.target.value)}
                                />
                              ) : (
                                <Input
                                  type="file"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (!file) return
                                    // In a real app, upload file and set URL
                                    // For now, setting a placeholder or simulated path
                                    setAuditValue(`uploads/audit_${file.name}`)
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </div>

                        {/* KYC Proof */}
                        <div className="space-y-4 p-4 rounded-lg bg-muted/30 border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-blue-500" />
                              <Label className="font-semibold">KYC Verification</Label>
                            </div>
                            <Switch checked={isKYC} onCheckedChange={setIsKYC} />
                          </div>

                          {isKYC && (
                            <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                              <div className="flex rounded-md bg-background p-0.5 border">
                                <button
                                  type="button"
                                  className={`flex-1 py-1 text-xs rounded-sm transition-all ${kycType === 'link' ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted'}`}
                                  onClick={() => setKycType('link')}
                                >
                                  Link
                                </button>
                                <button
                                  type="button"
                                  className={`flex-1 py-1 text-xs rounded-sm transition-all ${kycType === 'upload' ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted'}`}
                                  onClick={() => setKycType('upload')}
                                >
                                  Upload
                                </button>
                              </div>

                              {kycType === 'link' ? (
                                <Input
                                  placeholder="URL to KYC Proof"
                                  value={kycValue}
                                  onChange={(e) => setKycValue(e.target.value)}
                                />
                              ) : (
                                <Input
                                  type="file"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (!file) return
                                    setKycValue(`uploads/kyc_${file.name}`)
                                  }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="short-description">Short Description (Summary) *</Label>
                      <Input
                        id="short-description"
                        placeholder="Brief overview for the listing card (max 150 characters)..."
                        maxLength={150}
                        value={shortDescription || ""}
                        onChange={(e) => setShortDescription(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        {shortDescription.length}/150 characters. This appears on the project card.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Long Description (About) *</Label>
                      <Textarea
                        id="description"
                        placeholder="Detailed project information for the 'About' section..."
                        rows={6}
                        value={description || ""}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>

                    {/* Tokenomics Section */}
                    <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Tokenomics</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTokenomics([...tokenomics, { name: "", percent: 0, wallet: "" }])}
                        >
                          + Add Category
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Define how tokens are allocated (should total 100%)</p>

                      <div className="space-y-3">
                        {tokenomics.map((item, idx) => (
                          <div key={`tokenomic-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-3">
                              <Input
                                placeholder="Category"
                                value={item.name || ""}
                                onChange={(e) => {
                                  const updated = [...tokenomics]
                                  updated[idx].name = e.target.value
                                  setTokenomics(updated)
                                }}
                              />
                            </div>
                            <div className="col-span-2">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  placeholder="%"
                                  value={item.percent ?? ""}
                                  onChange={(e) => {
                                    const updated = [...tokenomics]
                                    updated[idx].percent = Number(e.target.value)
                                    setTokenomics(updated)
                                  }}
                                  min="0"
                                  max="100"
                                />
                                <span className="text-sm">%</span>
                              </div>
                            </div>
                            <div className="col-span-6">
                              <Input
                                placeholder="Wallet address (optional)"
                                value={item.wallet || ""}
                                onChange={(e) => {
                                  const updated = [...tokenomics]
                                  updated[idx].wallet = e.target.value
                                  setTokenomics(updated)
                                }}
                              />
                            </div>
                            <div className="col-span-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => setTokenomics(tokenomics.filter((_, i) => i !== idx))}
                              >
                                ✕
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span>Total:</span>
                        <span className={tokenomics.reduce((sum, i) => sum + i.percent, 0) === 100 ? "text-[#7ed957]" : "text-yellow-500"}>
                          {tokenomics.reduce((sum, i) => sum + i.percent, 0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="step-3" className="space-y-6 mt-0">
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold">Review & Launch</h2>
                    <p className="text-muted-foreground">Review your presale details before launching.</p>
                  </div>

                  <div className="space-y-4">
                    <Card className="p-4 bg-muted/50">
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Token Name:</span>
                          <span className="font-medium">{tokenInfo?.name || 'Not verified'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Symbol:</span>
                          <span className="font-medium">{tokenInfo?.symbol || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Presale Rate:</span>
                          <span className="font-medium">
                            {rateType === 'usd'
                              ? `$1 = ${presaleRate || '0'} ${tokenInfo?.symbol || 'tokens'}`
                              : `1 ${isTestnet ? 'tBNB' : 'BNB'} = ${presaleRate || '0'} ${tokenInfo?.symbol || 'tokens'}`
                            }
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Listing Rate:</span>
                          <span className="font-medium">
                            {rateType === 'usd'
                              ? `$1 = ${listingRate || presaleRate || '0'} ${tokenInfo?.symbol || 'tokens'}`
                              : `1 ${isTestnet ? 'tBNB' : 'BNB'} = ${listingRate || presaleRate || '0'} ${tokenInfo?.symbol || 'tokens'}`
                            }
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Soft Cap / Hard Cap:</span>
                          <span className="font-medium">
                            {softCap || '0'} / {hardCap || '0'} {rateType === 'usd' ? 'USD' : (isTestnet ? 'tBNB' : 'BNB')}
                          </span>
                        </div>
                        <div className="flex justify-between text-primary font-bold">
                          <span className="text-muted-foreground">Tokens for Presale:</span>
                          <span>
                            {(parseFloat(hardCap || "0") * parseFloat(presaleRate || "0")).toLocaleString()} {tokenInfo?.symbol || 'Tokens'}
                          </span>
                        </div>
                        <div className="flex justify-between text-primary font-bold">
                          <span className="text-muted-foreground">Tokens for Liquidity:</span>
                          <span>
                            {((parseFloat(hardCap || "0") * (parseFloat(liquidityPercent || "0") / 100)) * parseFloat(listingRate || presaleRate || "0")).toLocaleString()} {tokenInfo?.symbol || 'Tokens'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Min / Max Buy:</span>
                          <span className="font-medium">
                            {minContribution || '0'} / {maxContribution || '0'} {rateType === 'usd' ? 'USD' : (isTestnet ? 'tBNB' : 'BNB')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Liquidity %:</span>
                          <span className="font-medium">{liquidityPercent || '0'}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Start / End:</span>
                          <span className="font-medium text-xs">
                            {startTime ? new Date(startTime).toLocaleString() : '-'} → {endTime ? new Date(endTime).toLocaleString() : '-'}
                          </span>
                        </div>

                        <div className="border-t pt-3 mt-3">
                          <div className="flex justify-between mb-2">
                            <span className="text-muted-foreground">Security & Extras:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {isKYC && <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">KYC Verified</Badge>}
                            {isAudit && <Badge className="bg-[#7ed957]/10 text-[#6bc448] border-[#7ed957]/20">Audited</Badge>}
                            {lockLiquidity && <Badge className="bg-primary/10 text-primary border-primary/20">LP Locked ({lpLockDays} Days)</Badge>}
                            {isPremium && <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Premium Featured</Badge>}
                          </div>
                        </div>
                        <div className="border-t pt-3 mt-3">
                          <div className="flex justify-between mb-2">
                            <span className="text-muted-foreground">Accepted Currencies:</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(acceptedCurrencies)
                              .filter(([_, accepted]) => accepted)
                              .map(([currency]) => (
                                <Badge key={currency} variant="secondary">
                                  {currency}
                                </Badge>
                              ))}
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-muted-foreground">Supported Networks:</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(acceptedNetworks)
                              .filter(([_, accepted]) => accepted)
                              .map(([network]) => (
                                <Badge key={network} variant="secondary">
                                  {network}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      </div>
                    </Card>

                    {/* Premium Option */}
                    <Card className="p-4 md:p-6 border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 to-transparent">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="add-premium"
                          checked={isPremium}
                          onCheckedChange={(checked) => setIsPremium(checked as boolean)}
                        />
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center justify-between">
                            <Label
                              htmlFor="add-premium"
                              className="font-bold text-base md:text-lg cursor-pointer flex items-center gap-2"
                            >
                              <Icons8Icon name="star" size="lg" />
                              Premium
                              <Badge className="bg-yellow-500 text-black text-xs ml-2">Popular</Badge>
                            </Label>
                            <span className="font-bold text-yellow-600 text-lg">+{process.env.NEXT_PUBLIC_PRESALE_PREMIUM_FEE || '1'} {isTestnet ? 'tBNB' : 'BNB'}</span>
                          </div>
                          <p className="text-xs md:text-sm text-muted-foreground">
                            Best for serious projects seeking visibility
                          </p>
                          <ul className="text-xs text-muted-foreground mt-3 space-y-1.5">
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              Everything in Standard
                            </li>
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              Featured listing
                            </li>
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              Advanced analytics
                            </li>
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              Priority support
                            </li>
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              Full audit report
                            </li>
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              Marketing package
                            </li>
                            <li className="flex items-center gap-2">
                              <Star className="h-3 w-3 text-yellow-500" />
                              KYC verification
                            </li>
                          </ul>
                        </div>
                      </div>
                    </Card>

                    {/* Referral Settings */}
                    <Card className="p-4 md:p-6">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="enable-referral"
                          checked={enableReferral}
                          onCheckedChange={(checked) => setEnableReferral(checked as boolean)}
                        />
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center justify-between">
                            <Label
                              htmlFor="enable-referral"
                              className="font-bold text-base md:text-lg cursor-pointer flex items-center gap-2"
                            >
                              <Gift className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                              Enable Referral Program
                            </Label>
                          </div>
                          <p className="text-xs md:text-sm text-muted-foreground">
                            Allow users to earn commission by referring others to your presale
                          </p>
                          <div className="mt-4 space-y-3">
                            <div className="space-y-2">
                              <Label className="text-sm">Referral Commission (%)</Label>
                              <div className="flex items-center gap-3">
                                <Input
                                  type="number"
                                  placeholder="5"
                                  value={referralPercent}
                                  onChange={(e) => setReferralPercent(e.target.value)}
                                  className="w-24"
                                  min="1"
                                  max="10"
                                  disabled={!enableReferral}
                                />
                                <span className="text-sm text-muted-foreground">
                                  % of contribution goes to referrer
                                </span>
                              </div>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50 border">
                              <p className="text-xs text-muted-foreground">
                                <strong>Example:</strong> With 5% commission, if someone contributes 1 BNB through a referral link, the referrer earns 0.05 BNB.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>

                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="flex items-start gap-3">
                        <Icons8Icon name="dollar-coin" size="lg" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Total Fee</p>
                          <p className="text-2xl font-bold">
                            {(() => {
                              const standardFee = parseFloat(process.env.NEXT_PUBLIC_PRESALE_CREATION_FEE || '0.03')
                              const premiumFee = parseFloat(process.env.NEXT_PUBLIC_PRESALE_PREMIUM_FEE || '1')
                              const total = isPremium ? standardFee + premiumFee : standardFee
                              return `${total} ${isTestnet ? 'tBNB' : 'BNB'}`
                            })()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {isPremium
                              ? `Standard (${process.env.NEXT_PUBLIC_PRESALE_CREATION_FEE || '0.03'}) + Premium (${process.env.NEXT_PUBLIC_PRESALE_PREMIUM_FEE || '1'})`
                              : 'Standard launch fee'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between gap-4 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                  disabled={activeStep === 0}
                >
                  Previous
                </Button>
                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={() => {
                      // Validate current step before proceeding
                      if (activeStep === 0) {
                        // Step 0: Verify Token - require valid token address
                        if (!tokenAddress || tokenAddress.length !== 42) {
                          alert('Please enter a valid token address (42 characters starting with 0x)')
                          return
                        }
                        if (!tokenVerified) {
                          alert('Please wait for token verification to complete')
                          return
                        }
                      } else if (activeStep === 1) {
                        // Step 1: Presale Info - require rate/tokens, caps, times
                        if (isFairLaunch) {
                          if (!tokenAmount || parseFloat(tokenAmount) <= 0) {
                            alert('Please enter total tokens for sale')
                            return
                          }
                        } else {
                          if (!presaleRate || parseFloat(presaleRate) <= 0) {
                            alert('Please enter a valid presale rate')
                            return
                          }
                          if (!hardCap || parseFloat(hardCap) <= 0) {
                            alert('Please enter a valid hard cap')
                            return
                          }
                          if (parseFloat(softCap) >= parseFloat(hardCap)) {
                            alert('Soft cap must be less than hard cap')
                            return
                          }
                        }

                        if (!softCap || parseFloat(softCap) <= 0) {
                          alert('Please enter a valid soft cap')
                          return
                        }
                        if (!startTime) {
                          alert('Please set a start time')
                          return
                        }
                        if (!endTime) {
                          alert('Please set an end time')
                          return
                        }
                        if (new Date(startTime) >= new Date(endTime)) {
                          alert('End time must be after start time')
                          return
                        }
                      } else if (activeStep === 2) {
                        // Step 2: Additional Details - require logo and descriptions
                        if (!logoPreview) {
                          alert('Please upload a project logo')
                          return
                        }
                        if (!shortDescription || shortDescription.trim().length < 10) {
                          alert('Please enter a short description (at least 10 characters)')
                          return
                        }
                        if (!description || description.trim().length < 50) {
                          alert('Please enter a detailed description (at least 50 characters)')
                          return
                        }
                      }
                      setActiveStep(Math.min(steps.length - 1, activeStep + 1))
                    }}
                  >
                    Next Step
                  </Button>
                ) : (
                  <Button
                    className="gap-2"
                    onClick={handleLaunchPresale}
                    disabled={isPending || isConfirming || isFairLaunchPending || isFairLaunchConfirming}
                  >
                    <Icons8Icon name="rocket" size="md" />
                    {isApprovalStep && !isApproved
                      ? 'Step 1: Approve Tokens'
                      : 'Step 2: Create Presale'}
                  </Button>
                )}
              </div>
            </Card >
          </div >
        </div >
      </main >

      {/* Wallet Approval Modal - stays open during tx pending, confirming, OR relayer processing */}
      < Dialog open={isPending || isConfirming || isFairLaunchPending || isFairLaunchConfirming || isRelayerProcessing
      }>
        <DialogContent className="sm:max-w-md [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-center">
              {(isPending || isFairLaunchPending) ? 'Waiting for Approval' : 'Confirming Transaction'}
            </DialogTitle>
            {txStep.total > 0 && (
              <p className="text-center text-sm text-muted-foreground">
                Step {txStep.current} of {txStep.total}
              </p>
            )}
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-primary/20 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>

            {/* Progress dots */}
            {txStep.total > 1 && (
              <div className="flex gap-2">
                {Array.from({ length: txStep.total }, (_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${i + 1 < txStep.current
                      ? 'bg-[#7ed957]'
                      : i + 1 === txStep.current
                        ? 'bg-primary animate-pulse'
                        : 'bg-muted'
                      }`}
                  />
                ))}
              </div>
            )}

            <div className="text-center space-y-2">
              {txStep.description && (
                <p className="text-sm font-medium text-primary">{txStep.description}</p>
              )}
              {isPending && (
                <>
                  <p className="text-lg font-medium">Please confirm in your wallet</p>
                  <p className="text-sm text-muted-foreground">
                    Open your wallet and approve the transaction
                  </p>
                </>
              )}
              {isConfirming && (
                <>
                  <p className="text-lg font-medium">Transaction submitted</p>
                  <p className="text-sm text-muted-foreground">
                    Waiting for blockchain confirmation...
                  </p>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog >

      {/* SEO explainer — below the form, above footer */}
      <section className="container max-w-3xl mx-auto pb-16">
        <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
          <h2 className="text-base font-semibold text-foreground">How Token Presales Work on TokenKickstarter</h2>
          <p>
            A presale lets you raise funds before your token lists on a decentralized exchange. Set your own
            price, soft cap, hard cap, and contribution limits. Buyers pay in native tokens like BNB and ETH
            or stablecoins like USDT and USDC.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Live USD pricing</strong> — Chainlink oracles convert every payment to an accurate dollar value.</li>
            <li><strong>Auto liquidity lock</strong> — a portion of raised funds is locked as liquidity on launch.</li>
            <li><strong>Referral system</strong> — give buyers a 5% bonus when they share your presale link.</li>
            <li><strong>Multi-chain</strong> — deploy on BNB Chain, Ethereum, Base, Polygon, and more.</li>
          </ul>
          <p>
            Fill out the form above to configure your presale. You will need a connected wallet and enough
            gas to deploy the smart contract. The entire process takes about five minutes.
          </p>
        </div>
      </section>

      <Footer />
    </div >
  )
}
